import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type Word = { id: string; english: string; korean: string };
type Quiz = { id: string; word_pair_id: string; question: string; korean_hint: string; answer: string };
type Choice = { id: string; quiz_id: string; choice_text: string; is_correct: boolean };
type Pair = { id: string; word_a: Word; word_b: Word };

export default function Concept1Page() {
  const [words, setWords] = useState<Word[]>([]);
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [wordPicker, setWordPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [pairForm, setPairForm] = useState<any>(null);
  const [quizForm, setQuizForm] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<{type:string,id:string,label:string}|null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: ws }, { data: ps }, { data: qs }, { data: cs }] = await Promise.all([
      supabase.from('words').select('id, english, korean').order('english'),
      supabase.from('word_pairs').select('id, word_a_id, word_b_id'),
      supabase.from('concept1_quizzes').select('*'),
      supabase.from('quiz_choices').select('*'),
    ]);
    setWords(ws ?? []);
    const wordMap = Object.fromEntries((ws ?? []).map((w: Word) => [w.id, w]));
    setPairs((ps ?? []).map((p: any) => ({ id: p.id, word_a: wordMap[p.word_a_id], word_b: wordMap[p.word_b_id] })));
    setQuizzes(qs ?? []);
    setChoices(cs ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function addPairFromPicker() {
    if (picked.length !== 2) return;
    const { error } = await supabase.from('word_pairs').insert([{ word_a_id: picked[0], word_b_id: picked[1] }]);
    if (error) { alert(error.message); return; }
    setWordPicker(false);
    setPicked([]);
    setPickerSearch('');
    fetchAll();
  }

  function togglePick(id: string) {
    setPicked(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 2 ? [...prev, id] : prev
    );
  }

  async function savePair() {
    const payload = { word_a_id: pairForm.word_a_id, word_b_id: pairForm.word_b_id };
    if (pairForm.id) await supabase.from('word_pairs').update(payload).eq('id', pairForm.id);
    else await supabase.from('word_pairs').insert([payload]);
    setPairForm(null); fetchAll();
  }

  async function saveQuiz() {
    const { choices: choiceList, id, ...rest } = quizForm;
    if (id) {
      await supabase.from('concept1_quizzes').update(rest).eq('id', id);
      await supabase.from('quiz_choices').delete().eq('quiz_id', id);
      await supabase.from('quiz_choices').insert(choiceList.map((c: any) => ({ quiz_id: id, choice_text: c.text, is_correct: c.is_correct })));
    } else {
      const { data } = await supabase.from('concept1_quizzes').insert([rest]).select();
      const newId = data?.[0]?.id;
      if (newId) await supabase.from('quiz_choices').insert(choiceList.map((c: any) => ({ quiz_id: newId, choice_text: c.text, is_correct: c.is_correct })));
    }
    setQuizForm(null); fetchAll();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'pair') await supabase.from('word_pairs').delete().eq('id', deleteTarget.id);
    if (deleteTarget.type === 'quiz') await supabase.from('concept1_quizzes').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null); fetchAll();
  }

  function openNewQuiz(pairId: string) {
    setQuizForm({ word_pair_id: pairId, question: '', korean_hint: '', answer: '',
      choices: [{ text: '', is_correct: true }, { text: '', is_correct: false }] });
  }

  function openEditQuiz(q: Quiz) {
    const qChoices = choices.filter(c => c.quiz_id === q.id).map(c => ({ text: c.choice_text, is_correct: c.is_correct }));
    setQuizForm({ ...q, choices: qChoices.length ? qChoices : [{ text: '', is_correct: true }, { text: '', is_correct: false }] });
  }

  const filteredPairs = pairs.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.word_a?.english?.toLowerCase().includes(q) ||
      p.word_a?.korean?.toLowerCase().includes(q) ||
      p.word_b?.english?.toLowerCase().includes(q) ||
      p.word_b?.korean?.toLowerCase().includes(q)
    );
  });

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>🔀 Concept 1 — A vs B</h1>
          <div className={styles.dbBadge}>🗄 word_pairs · concept1_quizzes · quiz_choices</div>
          <span className={styles.count} style={{marginTop:4,display:'block'}}>
            단어 쌍 {pairs.length}개 · 퀴즈 {quizzes.length}개
          </span>
        </div>
        <div className={styles.actions}>
          <input className={`input ${styles.search}`} placeholder="단어 검색..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-ghost" onClick={fetchAll}>↺</button>
          <button className="btn btn-ghost" onClick={() => { setWordPicker(true); setPicked([]); setPickerSearch(''); }}>🔤 단어 선택</button>
          <button className="btn btn-primary" onClick={() => setPairForm({word_a_id:'',word_b_id:''})}>+ 단어 쌍</button>
        </div>
      </div>

      {/* Pairs with inline quizzes */}
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        {filteredPairs.map(p => {
          const pairQuizzes = quizzes.filter(q => q.word_pair_id === p.id);
          const isExpanded = expanded === p.id;
          return (
            <div key={p.id} style={{
              background:'rgba(255,255,255,0.1)',
              border:'1px solid rgba(255,255,255,0.18)',
              borderRadius:16,
              overflow:'hidden',
            }}>
              {/* Pair header */}
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',cursor:'pointer'}}
                onClick={() => setExpanded(isExpanded ? null : p.id)}>
                {/* Word A */}
                <div style={{flex:1,background:'rgba(155,127,212,0.2)',border:'1px solid rgba(155,127,212,0.35)',borderRadius:10,padding:'8px 14px'}}>
                  <div style={{fontSize:15,fontWeight:800,color:'white'}}>{p.word_a?.english}</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.5)',marginTop:2}}>{p.word_a?.korean}</div>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:'rgba(255,255,255,0.35)',flexShrink:0}}>VS</div>
                {/* Word B */}
                <div style={{flex:1,background:'rgba(232,100,154,0.15)',border:'1px solid rgba(232,100,154,0.3)',borderRadius:10,padding:'8px 14px'}}>
                  <div style={{fontSize:15,fontWeight:800,color:'white'}}>{p.word_b?.english}</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.5)',marginTop:2}}>{p.word_b?.korean}</div>
                </div>
                {/* Meta */}
                <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                  <span style={{
                    background: pairQuizzes.length > 0 ? 'rgba(100,200,150,0.2)' : 'rgba(255,255,255,0.08)',
                    border: `1px solid ${pairQuizzes.length > 0 ? 'rgba(100,200,150,0.4)' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,
                    color: pairQuizzes.length > 0 ? '#64C896' : 'rgba(255,255,255,0.4)',
                  }}>
                    퀴즈 {pairQuizzes.length}
                  </span>
                  <span style={{color:'rgba(255,255,255,0.3)',fontSize:12}}>{isExpanded ? '▲' : '▼'}</span>
                </div>
                {/* Actions */}
                <div style={{display:'flex',gap:6,flexShrink:0}} onClick={e => e.stopPropagation()}>
                  <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:11}}
                    onClick={() => setPairForm({id:p.id,word_a_id:p.word_a?.id,word_b_id:p.word_b?.id})}>편집</button>
                  <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:11}}
                    onClick={() => setDeleteTarget({type:'pair',id:p.id,label:`${p.word_a?.english} vs ${p.word_b?.english}`})}>삭제</button>
                </div>
              </div>

              {/* Quizzes panel */}
              {isExpanded && (
                <div style={{borderTop:'1px solid rgba(255,255,255,0.1)',padding:'16px 18px',background:'rgba(0,0,0,0.08)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,color:'rgba(255,255,255,0.4)',textTransform:'uppercase'}}>
                      퀴즈 목록
                    </div>
                    <button className="btn btn-ghost" style={{padding:'4px 12px',fontSize:11}}
                      onClick={() => openNewQuiz(p.id)}>+ 퀴즈 추가</button>
                  </div>

                  {pairQuizzes.length === 0 ? (
                    <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'12px 0'}}>퀴즈 없음 — 위 버튼으로 추가하세요</div>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      {pairQuizzes.map(q => {
                        const qChoices = choices.filter(c => c.quiz_id === q.id);
                        return (
                          <div key={q.id} style={{
                            background:'rgba(255,255,255,0.07)',
                            border:'1px solid rgba(255,255,255,0.12)',
                            borderRadius:12,padding:'12px 16px',
                          }}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,fontWeight:700,color:'white',marginBottom:4}}>{q.question}</div>
                                {q.korean_hint && (
                                  <div style={{fontSize:11,color:'rgba(255,255,255,0.45)',marginBottom:8}}>💬 {q.korean_hint}</div>
                                )}
                                {/* Choices */}
                                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                                  {qChoices.map(c => (
                                    <span key={c.id} style={{
                                      padding:'3px 12px',borderRadius:20,fontSize:12,fontWeight:600,
                                      background: c.is_correct ? 'rgba(100,200,150,0.2)' : 'rgba(255,255,255,0.08)',
                                      border: `1px solid ${c.is_correct ? 'rgba(100,200,150,0.5)' : 'rgba(255,255,255,0.15)'}`,
                                      color: c.is_correct ? '#64C896' : 'rgba(255,255,255,0.6)',
                                    }}>
                                      {c.is_correct && '✓ '}{c.choice_text}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div style={{display:'flex',gap:6,flexShrink:0}}>
                                <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:11}} onClick={() => openEditQuiz(q)}>편집</button>
                                <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:11}}
                                  onClick={() => setDeleteTarget({type:'quiz',id:q.id,label:q.question})}>삭제</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pair Modal */}
      {pairForm && (
        <div className="modal-overlay" onClick={() => setPairForm(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>단어 쌍 {pairForm.id ? '편집' : '추가'}</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setPairForm(null)}>✕</button>
            </div>
            {(['word_a_id','word_b_id'] as const).map(key => (
              <div key={key} style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>
                  {key === 'word_a_id' ? 'Word A' : 'Word B'}
                </label>
                <select className="input" value={pairForm[key]} onChange={e => setPairForm((p: any) => ({...p, [key]: e.target.value}))}>
                  <option value="">선택하세요</option>
                  {words.map(w => <option key={w.id} value={w.id}>{w.english} ({w.korean})</option>)}
                </select>
              </div>
            ))}
            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setPairForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={savePair}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {quizForm && (
        <div className="modal-overlay" onClick={() => setQuizForm(null)}>
          <div className="modal-box" style={{width:580,maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>퀴즈 {quizForm.id ? '편집' : '추가'}</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setQuizForm(null)}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>단어 쌍</label>
                <select className="input" value={quizForm.word_pair_id} onChange={e => setQuizForm((p: any) => ({...p, word_pair_id: e.target.value}))}>
                  {pairs.map(p => <option key={p.id} value={p.id}>{p.word_a?.english} vs {p.word_b?.english}</option>)}
                </select>
              </div>
              {[{key:'question',label:'Question (빈칸은 ___)'},{key:'korean_hint',label:'Korean Hint'},{key:'answer',label:'Answer (정답)'}].map(({key,label}) => (
                <div key={key}>
                  <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>{label}</label>
                  <input className="input" value={quizForm[key] ?? ''} onChange={e => setQuizForm((p: any) => ({...p, [key]: e.target.value}))} />
                </div>
              ))}
              <div>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:10}}>선택지</label>
                {quizForm.choices.map((c: any, i: number) => (
                  <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                    <input className="input" style={{flex:1}} placeholder={`선택지 ${i+1}`} value={c.text}
                      onChange={e => setQuizForm((p: any) => { const ch=[...p.choices]; ch[i]={...ch[i],text:e.target.value}; return {...p,choices:ch}; })} />
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,whiteSpace:'nowrap',cursor:'pointer'}}>
                      <input type="radio" checked={c.is_correct} onChange={() =>
                        setQuizForm((p: any) => ({ ...p, choices: p.choices.map((x: any, j: number) => ({...x, is_correct: i===j})) }))} />
                      정답
                    </label>
                    {quizForm.choices.length > 2 && (
                      <button className="btn btn-danger" style={{padding:'4px 8px',fontSize:12}}
                        onClick={() => setQuizForm((p: any) => ({...p, choices: p.choices.filter((_: any, j: number) => j!==i)}))}>✕</button>
                    )}
                  </div>
                ))}
                <button className="btn btn-ghost" style={{fontSize:12,marginTop:4}}
                  onClick={() => setQuizForm((p: any) => ({...p, choices: [...p.choices, {text:'',is_correct:false}]}))}>
                  + 선택지 추가
                </button>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:24,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setQuizForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={saveQuiz}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* Word Picker Modal */}
      {wordPicker && (
        <div className="modal-overlay" onClick={() => setWordPicker(false)}>
          <div className="modal-box" style={{width:520,maxHeight:'80vh',display:'flex',flexDirection:'column'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <h2 style={{fontSize:18,fontWeight:800}}>단어 선택 <span style={{fontSize:13,fontWeight:400,color:'rgba(255,255,255,0.4)'}}>2개 선택 후 추가</span></h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setWordPicker(false)}>✕</button>
            </div>

            {/* 선택된 단어 미리보기 */}
            <div style={{display:'flex',gap:8,marginBottom:14,minHeight:48,alignItems:'center'}}>
              {[0,1].map(i => {
                const w = words.find(w => w.id === picked[i]);
                return (
                  <div key={i} style={{
                    flex:1,borderRadius:10,padding:'8px 14px',
                    background: w ? (i===0 ? 'rgba(155,127,212,0.25)' : 'rgba(232,100,154,0.2)') : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${w ? (i===0 ? 'rgba(155,127,212,0.4)' : 'rgba(232,100,154,0.35)') : 'rgba(255,255,255,0.1)'}`,
                    display:'flex', alignItems:'center', justifyContent: w ? 'space-between' : 'center',
                  }}>
                    {w ? (
                      <>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,color:'white'}}>{w.english}</div>
                          <div style={{fontSize:11,color:'rgba(255,255,255,0.45)'}}>{w.korean}</div>
                        </div>
                        <button onClick={() => setPicked(prev => prev.filter(x => x !== w.id))}
                          style={{color:'rgba(255,255,255,0.3)',fontSize:12,background:'none',border:'none',cursor:'pointer'}}>✕</button>
                      </>
                    ) : (
                      <span style={{fontSize:12,color:'rgba(255,255,255,0.25)'}}>{i===0 ? 'Word A' : 'Word B'}</span>
                    )}
                  </div>
                );
              })}
              {picked.length === 2 && (
                <button className="btn btn-primary" style={{flexShrink:0,padding:'8px 16px'}} onClick={addPairFromPicker}>
                  추가
                </button>
              )}
            </div>

            <input className="input" placeholder="단어 검색..." value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)} style={{marginBottom:10}} />

            <div style={{overflowY:'auto',flex:1,display:'flex',flexDirection:'column',gap:4}}>
              {words
                .filter(w => !pickerSearch || `${w.english} ${w.korean}`.toLowerCase().includes(pickerSearch.toLowerCase()))
                .map(w => {
                  const isPicked = picked.includes(w.id);
                  const pickIdx = picked.indexOf(w.id);
                  return (
                    <button key={w.id} onClick={() => togglePick(w.id)} style={{
                      display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:10,
                      background: isPicked ? (pickIdx===0 ? 'rgba(155,127,212,0.2)' : 'rgba(232,100,154,0.15)') : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isPicked ? (pickIdx===0 ? 'rgba(155,127,212,0.4)' : 'rgba(232,100,154,0.35)') : 'rgba(255,255,255,0.08)'}`,
                      cursor: picked.length === 2 && !isPicked ? 'not-allowed' : 'pointer',
                      opacity: picked.length === 2 && !isPicked ? 0.4 : 1,
                      textAlign:'left', transition:'all .1s',
                    }}>
                      <span style={{
                        width:20,height:20,borderRadius:6,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
                        fontSize:11,fontWeight:800,
                        background: isPicked ? (pickIdx===0 ? 'rgba(155,127,212,0.6)' : 'rgba(232,100,154,0.5)') : 'rgba(255,255,255,0.1)',
                        color: isPicked ? '#fff' : 'rgba(255,255,255,0.3)',
                      }}>{isPicked ? (pickIdx===0 ? 'A' : 'B') : ''}</span>
                      <div>
                        <div style={{fontSize:14,fontWeight:600,color:'white'}}>{w.english}</div>
                        <div style={{fontSize:11,color:'rgba(255,255,255,0.45)'}}>{w.korean}</div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="삭제 확인" message={`"${deleteTarget.label}"을 삭제할까요?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}
